import { useRef, useState } from 'react';
import type { HoldRecorder } from './useHoldTurn.js';

const CANCEL_THRESHOLD = 64; // px a la izquierda para armar cancelar
const LOCK_THRESHOLD = 56; // px hacia arriba para fijar la grabación
// Umbrales de tap GENEROSOS: en touch real un tap casual llega a ~400ms con
// varios px de rolido del pulgar — con 300ms/12px muchos taps caían al carril
// "hold → enviar" y el usuario sentía que el candado no funcionaba.
const TAP_LOCK_MS = 450; // soltar antes de esto (sin arrastre) = tap → fija
const TAP_SLOP_PX = 18; // arrastre máximo para que el gesto siga contando como tap

export interface HoldGestureHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}

/**
 * Gesto push-to-talk estilo WhatsApp: pointer down arranca la grabación,
 * soltar envía, arrastrar a la IZQUIERDA arma "cancelar" (soltar ahí
 * descarta) y arrastrar hacia ARRIBA la FIJA (`recorder.lock()`) — la
 * grabación sigue sin sostener y el cierre pasa a los botones
 * enviar/descartar de la UI. Al fijar, el gesto termina acá mismo: el
 * caller puede desmontar/reemplazar el botón sin romper nada.
 *
 * TAP RÁPIDO (WhatsApp actual): soltar antes de `TAP_LOCK_MS` sin haber
 * arrastrado también FIJA en vez de enviar — un toque al mic deja la
 * grabación abierta a manos ocupadas. De paso elimina el turno-fantasma
 * de un tap accidental (antes ese soltar instantáneo commiteaba audio
 * casi vacío al STT).
 *
 * El elemento que recibe los handlers debe tener `touch-action: none` (CSS)
 * para que el drag no dispare scroll, y NO desmontarse mientras dura el hold
 * (pointer capture).
 */
export function useHoldGesture(
  recorder: HoldRecorder,
  disabled: boolean,
): { cancelArmed: boolean; handlers: HoldGestureHandlers } {
  const holdRef = useRef(false);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const downAtRef = useRef(0);
  const draggedRef = useRef(false);
  const armedRef = useRef(false);
  const [cancelArmed, setCancelArmed] = useState(false);

  const setArmed = (v: boolean): void => {
    armedRef.current = v;
    setCancelArmed(v);
  };

  const onPointerDown = (e: React.PointerEvent): void => {
    if (disabled) return;
    if (e.button !== 0) return;
    // El turno anterior todavía manda su post-roll: `start()` no abriría otro,
    // así que el gesto no se arma (si no, soltar/deslizar operaría sobre un
    // turno que no existe — deslizar descartaría el que está en vuelo).
    if (!recorder.recording && recorder.postRolling === true) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    holdRef.current = true;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    downAtRef.current = performance.now();
    draggedRef.current = false;
    setArmed(false);
    if (!recorder.recording) recorder.start();
  };

  const onPointerMove = (e: React.PointerEvent): void => {
    if (!holdRef.current) return;
    const dx = e.clientX - startXRef.current;
    const dy = e.clientY - startYRef.current;
    if (Math.hypot(dx, dy) > TAP_SLOP_PX) {
      draggedRef.current = true;
    }
    // Izquierda arma cancelar; volver a la derecha desarma (como WhatsApp).
    const armed = dx < -CANCEL_THRESHOLD;
    if (armed !== armedRef.current) setArmed(armed);
    // Arriba (sin cancelar armado) fija: el hold termina acá, sin enviar.
    if (!armed && dy < -LOCK_THRESHOLD) {
      holdRef.current = false;
      setArmed(false);
      recorder.lock();
    }
  };

  const finish = (): void => {
    if (!holdRef.current) return;
    holdRef.current = false;
    const armed = armedRef.current;
    setArmed(false);
    // OJO: acá NO se chequea `recorder.recording` — en un tap muy rápido el
    // up llega antes del re-render y ese estado del closure sigue en false
    // (el down ya arrancó la grabación igual). Los métodos del recorder
    // guardan por ref sincrónica, así que llamarlos siempre es seguro.
    const dt = performance.now() - downAtRef.current;
    // Tap rápido sin arrastre → fijar (WhatsApp actual); hold real → enviar.
    const isTap = !draggedRef.current && dt < TAP_LOCK_MS;
    if (armed) {
      recorder.cancel();
      return;
    }
    if (isTap) recorder.lock();
    else recorder.send();
  };

  return {
    cancelArmed,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finish,
      onPointerCancel: finish,
      onContextMenu: (e) => e.preventDefault(),
    },
  };
}
